import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Sparkles, X } from 'lucide-react';
import { createChatSession, sendMessage } from '../lib/openai-chatbot';
import { useAnnounce } from '../hooks/useAnnounce';
import ReactMarkdown from 'react-markdown';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  functionCall?: {
    name: string;
    args: any;
  };
}

interface QuickReply {
  text: string;
  value: string;
}

/**
 * FloatingChatWidget Component
 * 
 * A modern, well-styled floating chatbot widget that appears in the bottom-right corner
 * of the screen. Includes smooth animations, mobile responsiveness, and utilizes
 * the Gemini API with structured output for enhanced responses.
 */
const FloatingChatWidget: React.FC = () => {
  // Chat UI state
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [chatSession, setChatSession] = useState<any>(null);
  
  // State for quick reply buttons
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const announce = useAnnounce();
  
  // Initialize chat session and load any saved conversation
  useEffect(() => {
    if (!chatSession) {
      const session = createChatSession();
      setChatSession(session);
      
      // Add initial welcome message
      setMessages([{
        role: 'assistant',
        content: "Hi there! I'm your JMEFit Assistant. How can I help you with your fitness journey today?",
        timestamp: new Date()
      }]);

      // Set initial quick replies
      setQuickReplies([
        { text: 'Programs', value: 'What programs do you offer?' },
        { text: 'Pricing', value: 'How much does it cost?' },
        { text: 'Sign Up', value: 'How do I sign up?' },
        { text: 'Contact', value: 'How can I contact support?' },
      ]);
    }
  }, [chatSession]);
  
  // Scroll to bottom of chat when messages update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);
  
  const handleToggleChat = () => {
    setIsOpen(!isOpen);
  };
  
  const handleCloseChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
  };
  
  // Handle quick reply button clicks
  const handleQuickReplyClick = (value: string) => {
    setInputValue(value);
    // Submit the message immediately
    setTimeout(() => handleSendMessage(), 100);
  };
  
  const handleSendMessage = async () => {
    if (!(inputValue || '').trim() || isTyping || !chatSession) return;

    const userMessage = (inputValue || '').trim();
    setInputValue('');

    // Add user message to chat
    setMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: userMessage,
        timestamp: new Date()
      }
    ]);

    // Show typing indicator
    setIsTyping(true);

    try {
      const response = await sendMessage(chatSession, userMessage);

      const assistantMessage = response.message || String(response);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: assistantMessage,
          timestamp: new Date()
        }
      ]);
      announce('New message from JMEFit Assistant');

      // Use quick replies from response if available
      if (response.quick_replies && response.quick_replies.length > 0) {
        setQuickReplies(response.quick_replies.map((qr: any) => ({
          text: qr.text,
          value: qr.value
        })));
      } else {
        setQuickReplies([
          { text: 'Programs', value: 'What programs do you offer?' },
          { text: 'Pricing', value: 'How much does it cost?' },
          { text: 'Sign Up', value: 'How do I sign up?' },
          { text: 'Contact', value: 'How can I contact support?' },
        ]);
      }
    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "I'm sorry, I'm having trouble responding right now. Please try again or contact us at info@jmefit.com.",
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };
  
  const widgetPosition = { bottom: '24px', right: '24px' };

  return (
    <div 
      ref={widgetRef}
      className="fixed z-50 flex flex-col items-end"
      style={widgetPosition}
    >
      {/* Chat Window */}
      {isOpen && (
        <div className="bg-white rounded-xl shadow-2xl mb-3 flex flex-col overflow-hidden w-[350px] max-w-[90vw] h-[500px] max-h-[80vh]">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-jme-purple to-jme-cyan p-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-white" />
              <h3 className="text-white font-bold">JMEFit Assistant</h3>
            </div>
            <button
              onClick={handleCloseChat}
              className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>
          
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3" aria-live="polite" aria-label="Chat messages">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[85%] rounded-lg p-3 ${
                    message.role === 'user' 
                      ? 'bg-gradient-to-r from-jme-purple to-jme-cyan text-white rounded-tr-none shadow-md' 
                      : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none shadow-md'
                  }`}
                >
                  {message.role === 'user' ? (
                    <div className="text-sm">{message.content}</div>
                  ) : (
                    <div className="text-sm markdown-content">
                      <ReactMarkdown components={{
                        p: ({children}) => <p className="mb-2">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-4 my-2">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-4 my-2">{children}</ol>,
                        li: ({children}) => <li className="mb-1">{children}</li>,
                        a: ({href, children}) => <a href={href} className="text-blue-500 underline">{children}</a>,
                        strong: ({children}) => <span className="font-bold text-jme-purple">{children}</span>,
                        h1: ({children}) => <h1 className="text-lg font-bold mt-3 mb-2">{children}</h1>,
                        h2: ({children}) => <h2 className="text-md font-bold mt-3 mb-2">{children}</h2>,
                        h3: ({children}) => <h3 className="text-md font-bold mt-2 mb-1">{children}</h3>,
                      }}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  )}
                  
                  <div className="text-xs mt-2 opacity-60 text-right">
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute:'2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 text-gray-800 rounded-lg rounded-tl-none max-w-[85%] p-3 shadow-md">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Quick Reply Buttons */}
          {quickReplies.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 border-t border-gray-200">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickReplyClick(reply.value)}
                  className="px-4 py-2 text-sm bg-gradient-to-r from-jme-purple to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-full transition-colors text-white font-medium shadow-sm"
                >
                  {reply.text}
                </button>
              ))}
            </div>
          )}
          
          {/* Input Area */}
          <div className="p-3 border-t border-gray-100">
            <div className="flex gap-2">
              <input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Ask me about JMEFit..."
                className="flex-1 border-2 border-gray-200 rounded-lg py-3 px-4 focus:outline-none focus:ring-2 focus:ring-jme-purple focus:border-transparent text-sm shadow-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!(inputValue || '').trim() || isTyping}
                className="h-[45px] w-[45px] flex items-center justify-center bg-gradient-to-r from-jme-purple to-jme-cyan text-white rounded-full hover:shadow-lg disabled:opacity-50 transition-all"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Bubble Button */}
      {!isOpen && (
        <button
          onClick={handleToggleChat}
          className="rounded-full shadow-lg w-14 h-14 flex items-center justify-center bg-gradient-to-r from-jme-purple to-jme-cyan"
          aria-label="Chat with JMEFit Assistant"
        >
          <MessageSquare size={24} className="text-white" />
          <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full"></span>
        </button>
      )}
    </div>
  );
};

export default FloatingChatWidget;
